import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navItems = [
  { path: '/terapist-panel/dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
  { path: '/terapist-panel/profilim', label: 'Profilim', icon: 'ri-user-settings-line' },
  { path: '/terapist-panel/gorunurluk', label: 'Görünürlük Ayarlarım', icon: 'ri-eye-line' },
  { path: '/terapist-panel/firsatlar', label: 'İşletme Fırsatları', icon: 'ri-briefcase-line' },
  { path: '/terapist-panel/talepler', label: 'İletişim Talepleri', icon: 'ri-mail-send-line', badge: 3 },
  { path: '/terapist-panel/gorusmeler', label: 'Görüşmelerim', icon: 'ri-chat-3-line', badge: 2 },
  { path: '/terapist-panel/favoriler', label: 'Favorilerim', icon: 'ri-heart-3-line' },
  { path: '/terapist-panel/gecmis', label: 'Geçmiş Görüşmeler', icon: 'ri-history-line' },
  { path: '/terapist-panel/notlarim', label: 'Notlarım', icon: 'ri-sticky-note-line' },
];

interface PanelLayoutProps {
  children: React.ReactNode;
}

export default function PanelLayout({ children }: PanelLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen bg-off-white flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-gray-100 fixed left-0 top-0 h-screen z-40">
        <div className="p-5 border-b border-gray-100">
          <Link to="/" className="flex items-center gap-2 text-deep-navy-500 font-bold text-lg">
            <span className="w-8 h-8 rounded-lg bg-dark-teal-500 flex items-center justify-center text-white text-sm font-bold">
              S
            </span>
            <span>SpaProBridge</span>
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-dark-teal-50 text-dark-teal-500'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-deep-navy-500'
                    }`}
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    <span className="flex-1 whitespace-nowrap">{item.label}</span>
                    {item.badge && (
                      <span className="ml-auto px-2 py-0.5 rounded-full bg-dark-teal-500 text-white text-[10px] font-bold">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
          <div className="mt-6 pt-4 border-t border-gray-100 px-3">
            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Ayarlar</span>
            <ul className="mt-2 space-y-1">
              <li>
                <Link
                  to="/terapist-panel/bildirim-ayarlari"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-deep-navy-500 transition-colors"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-notification-3-line text-base" />
                  </span>
                  Bildirim Ayarları
                </Link>
              </li>
              <li>
                <Link
                  to="/terapist-panel/destek"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-deep-navy-500 transition-colors"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-customer-service-line text-base" />
                  </span>
                  Destek & Şikayet
                </Link>
              </li>
              <li>
                <Link
                  to="/terapist-panel/hesap"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-deep-navy-500 transition-colors"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-settings-3-line text-base" />
                  </span>
                  Hesap Ayarları
                </Link>
              </li>
            </ul>
          </div>
        </nav>
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-dark-teal-100 flex items-center justify-center text-dark-teal-500 font-bold text-sm">
              AY
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-deep-navy-500 truncate">Ayşe Y.</p>
              <p className="text-xs text-gray-400">Onaylandı</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Topbar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-100 z-40 flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="w-10 h-10 flex items-center justify-center text-deep-navy-500 cursor-pointer"
        >
          <i className={`ri-${sidebarOpen ? 'close' : 'menu'}-line text-xl`} />
        </button>
        <Link to="/" className="flex items-center gap-2 text-deep-navy-500 font-bold text-lg">
          <span className="w-7 h-7 rounded-md bg-dark-teal-500 flex items-center justify-center text-white text-xs font-bold">S</span>
          <span>SpaProBridge</span>
        </Link>
        <div className="w-8 h-8 rounded-full bg-dark-teal-100 flex items-center justify-center text-dark-teal-500 font-bold text-xs">
          AY
        </div>
      </div>

      {/* Mobile Sidebar Drawer */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-white h-full flex flex-col shadow-xl">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2 text-deep-navy-500 font-bold text-lg" onClick={() => setSidebarOpen(false)}>
                <span className="w-8 h-8 rounded-lg bg-dark-teal-500 flex items-center justify-center text-white text-sm font-bold">S</span>
                <span>SpaProBridge</span>
              </Link>
              <button onClick={() => setSidebarOpen(false)} className="w-8 h-8 flex items-center justify-center text-gray-500 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-4 px-3">
              <ul className="space-y-1">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-dark-teal-50 text-dark-teal-500'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-deep-navy-500'
                        }`}
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${item.icon} text-base`} />
                        </span>
                        <span className="flex-1">{item.label}</span>
                        {item.badge && (
                          <span className="ml-auto px-2 py-0.5 rounded-full bg-dark-teal-500 text-white text-[10px] font-bold">
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-6 pt-4 border-t border-gray-100 px-3">
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Ayarlar</span>
                <ul className="mt-2 space-y-1">
                  <li>
                    <Link
                      to="/terapist-panel/bildirim-ayarlari"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-deep-navy-500 transition-colors"
                    >
                      <span className="w-5 h-5 flex items-center justify-center"><i className="ri-notification-3-line text-base" /></span>
                      Bildirim Ayarları
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/terapist-panel/destek"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-deep-navy-500 transition-colors"
                    >
                      <span className="w-5 h-5 flex items-center justify-center"><i className="ri-customer-service-line text-base" /></span>
                      Destek & Şikayet
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/terapist-panel/hesap"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-deep-navy-500 transition-colors"
                    >
                      <span className="w-5 h-5 flex items-center justify-center"><i className="ri-settings-3-line text-base" /></span>
                      Hesap Ayarları
                    </Link>
                  </li>
                </ul>
              </div>
            </nav>
            <div className="p-4 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-dark-teal-100 flex items-center justify-center text-dark-teal-500 font-bold text-sm">
                  AY
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-deep-navy-500">Ayşe Y.</p>
                  <p className="text-xs text-gray-400">Onaylandı</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 bg-black/30" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 min-h-screen">
        <div className="pt-16 lg:pt-0 px-4 md:px-6 lg:px-8 py-6 lg:py-8">
          {children}
        </div>
      </main>
    </div>
  );
}